/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import type { HTMLAttributes } from 'react';
import { type ForwardedRef, type ReactNode, createContext, forwardRef, useState } from 'react';
import { useFocusWithin, useHover } from 'react-aria';
import { type ContextValue, DEFAULT_SLOT, Provider, ToolbarContext, useContextProps, useRenderProps } from 'react-aria-components';
import { SidebarCollapsedStrokeIcon } from '../../icons/sidebar-collapsed-stroke/sidebar-collapsed-stroke.icon';
import { SidebarExpandedStrokeIcon } from '../../icons/sidebar-expanded-stroke/sidebar-expanded-stroke.icon';
import { $ } from '../../tokens.stylex';
import { AvatarContext } from '../avatar/avatar.component';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { IconContext } from '../icon/icon.component';
import { LinkIconButtonContext } from '../link-icon-button/link-icon-button.component';
import { ListBoxContext } from '../list-box/list-box.component';
import { SearchFieldContext } from '../search-field/search-field.component';

interface VerticalNavigationRenderProps {
  isOpen: boolean;
  isHovered: boolean;
  isFocusWithin: boolean;
}

interface VerticalNavigationProps extends Omit<HTMLAttributes<HTMLElement>, 'className' | 'style' | 'children'> {
  /** Whether the vertical navigation should collapse to a 60px rail. (Default: false) */
  collapseToRail?: boolean;
  /** Whether the vertical navigation is open by default (controlled). */
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  children?: ReactNode | ((opts: VerticalNavigationRenderProps) => ReactNode);
  style?: stylex.StyleXStyles;
}

const VerticalNavigationContext = createContext<ContextValue<VerticalNavigationProps, HTMLElement>>({});

const VerticalNavigation = forwardRef((props: VerticalNavigationProps, ref: ForwardedRef<HTMLElement>) => {
  [props, ref] = useContextProps(props, ref, VerticalNavigationContext);

  const { style, children, isOpen = false, onOpenChange, collapseToRail = false, ...coreProps } = props;

  const [isFocusWithin, setIsFocusWithin] = useState(false);

  const { hoverProps, isHovered } = useHover({});
  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: (e) => e.target.ariaLabel !== 'Expand sidebar' && setIsFocusWithin(true),
    onBlurWithin: () => setIsFocusWithin(false),
  });

  let renderProps = useRenderProps({
    children: children,
    values: {
      isOpen,
      isHovered,
      isFocusWithin,
    }
  });

  return (
    <nav
      ref={ref}
      data-hovered={isHovered || undefined}
      data-focus-within={isFocusWithin || undefined}
      {...stylex.props(stylex.defaultMarker(), styles.base, isOpen ? styles.opened : collapseToRail ? styles.collapsed : styles.hidden, style)}
      {...hoverProps}
      {...focusWithinProps}
      {...coreProps}>
      <Provider values={[
        [AvatarContext, {
          size: 32,
        }],
        [ListBoxContext, {
          selectionMode: 'single',
          selectionBehavior: "replace",
          disallowEmptySelection: true,
          "aria-label": "Main navigation",
          style: listBoxStyles.base,
        }],
        [SearchFieldContext, {
          size: 36,
          variant: 'filled',
          isRounded: true,
        }],
        [IconButtonContext, {
          slots: {
            [DEFAULT_SLOT]: {
              variant: 'ghost',
              size: 40
            },
            [VERTICAL_NAVIGATION_SLOTS.LOGO_OPEN_ICON_BUTTON]: {
              variant: 'ghost',
              size: 40,
              'aria-label': 'Expand sidebar',
              children: <SidebarExpandedStrokeIcon />,
              onPress: () => onOpenChange?.(true),
            },
            [VERTICAL_NAVIGATION_SLOTS.OPEN_ICON_BUTTON]: {
              variant: 'ghost',
              size: 40,
              'aria-label': 'Expand sidebar',
              children: <SidebarExpandedStrokeIcon />,
              onPress: () => onOpenChange?.(true),
            },
            [VERTICAL_NAVIGATION_SLOTS.CLOSE_ICON_BUTTON]: {
              variant: 'ghost',
              size: 40,
              'aria-label': 'Collapse sidebar',
              children: <SidebarCollapsedStrokeIcon />,
              onPress: () => onOpenChange?.(false),
            },
          }
        }],
        [LinkIconButtonContext, {
          slots: {
            [DEFAULT_SLOT]: {
              variant: 'ghost',
              size: isOpen ? 32 : collapseToRail ? 40 : 32,
              style: {
                icon: (linkIconButtonRenderProps) => linkIconButtonRenderProps.isCurrent && linkIconButtonStyles.current,
              },
            },
            [VERTICAL_NAVIGATION_SLOTS.OPEN_ICON_BUTTON]: {
              variant: 'ghost',
              size: 40,
              'aria-label': 'Expand sidebar',
              children: <SidebarExpandedStrokeIcon />,
              onPress: () => onOpenChange?.(true),
            },
            [VERTICAL_NAVIGATION_SLOTS.CLOSE_ICON_BUTTON]: {
              variant: 'ghost',
              size: 32,
              'aria-label': 'Collapse sidebar',
              children: <SidebarCollapsedStrokeIcon />,
              onPress: () => onOpenChange?.(false),
            },
          }
        }],
        [ToolbarContext, {
          slots: {
            [DEFAULT_SLOT]: {
              ...stylex.props(toolbarStyles.base),
              orientation: isOpen ? 'horizontal' : 'vertical',
            },
            [VERTICAL_NAVIGATION_SLOTS.AVATAR_TOOLBAR]: {
              ...stylex.props(
                toolbarStyles.base,
                isOpen ? toolbarStyles.avatarHorizontal : collapseToRail ? toolbarStyles.avatarVertical : toolbarStyles.avatarHorizontal
              ),
              orientation: isOpen ? 'horizontal' : 'vertical',
            },
            [VERTICAL_NAVIGATION_SLOTS.LOGO_TOOLBAR]: {
              ...stylex.props(toolbarStyles.base, toolbarStyles.logo),
              orientation: 'horizontal',
            },
          }
        }],
        [IconContext, {
          slots: {
            [DEFAULT_SLOT]: {},
            [VERTICAL_NAVIGATION_SLOTS.LOGO]: {
              style: [logoStyles.base, isOpen && logoStyles.indented],
            }
          }
        }],
      ]}>
        {renderProps.children}
      </Provider>
    </nav>
  );
});

const styles = stylex.create({
  base: {
    padding: 8,
    gap: 24,
    backgroundColor: $.surfaceBGSecondary,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    transitionDuration: '0.3s',
    transitionProperty: 'width, transform',
    transitionTimingFunction: 'ease',
    borderRightColor: $.border04,
    borderRightStyle: 'solid',
    borderRightWidth: 1,
    height: '100%',
  },
  collapsed: {
    width: 56,
  },
  opened: {
    width: 240,
  },
  hidden: {
    transform: 'translateX(-100%)',
    width: 240,
  },
});

const listBoxStyles = stylex.create({
  base: { height: '100%' },
});

const logoStyles = stylex.create({
  base: { marginRight: 'auto', },
  indented: { marginLeft: 12 }
});

const toolbarStyles = stylex.create({
  base: {
    alignItems: 'center',
    display: 'flex',
    flexBasis: 0,
    flexDirection: 'column',
    flexGrow: 1,
    flexShrink: 0,
    marginRight: 'auto',
    width: 40,
  },
  avatarVertical: {
    gap: 8,
    justifyContent: 'flex-end',
    marginRight: 'auto',
  },
  avatarHorizontal: {
    gap: 12,
    alignContent: 'flex-start',
    display: 'flex',
    flexDirection: 'row',
    paddingLeft: 4,
  },
  logo: {
    overflow: 'hidden',
    paddingBlock: 4,
    alignSelf: 'stretch',
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: 40,
    width: '100%',
  },
});

const linkIconButtonStyles = stylex.create({
  current: {
    stroke: $.iconPrimary,
    strokeWidth: 1.5,
  }
});

const VERTICAL_NAVIGATION_SLOTS = {
  LOGO: 'logo',
  LOGO_OPEN_ICON_BUTTON: 'logo-open-icon-button',
  LOGO_TOOLBAR: 'logo-toolbar',
  AVATAR_TOOLBAR: 'avatar-toolbar',
  OPEN_ICON_BUTTON: 'open-icon-button',
  CLOSE_ICON_BUTTON: 'close-icon-button',
};

export { VERTICAL_NAVIGATION_SLOTS, VerticalNavigation, VerticalNavigationContext, type VerticalNavigationProps };
